import React, { useState, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Panel,
} from '@xyflow/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import MetricGroupNode from './MetricGroupNode';

// Available error types for scenarios
const ERROR_TYPES = [
  { id: 'abr', label: 'ABR Error', description: 'All streams fail' },
  { id: 'cdn', label: 'CDN Error', description: 'CDN streams only' },
  { id: 'origin', label: 'Origin Error', description: 'Origin streams only' },
  { id: 'header_check', label: 'Header Check', description: 'Header validation error' },
  { id: 'black_screen', label: 'Black Screen', description: 'Shows black screen VOD' },
];

// Available formats for format-specific errors
const AVAILABLE_FORMATS = ['hls', 'dash'];

// Common CDNs for CDN-specific errors
const AVAILABLE_CDNS = ['CLOUDFRONT', 'AKAMAI', 'FASTLY', 'ORIGIN'];

// The three status bands a group can be in. Each metric carries one value per
// band, and a flag for whether it reads as healthy while the group sits there.
const METRIC_BANDS = [
  { key: 'good', label: 'Good', bg: 'success' },
  { key: 'warning', label: 'Warn', bg: 'warning' },
  { key: 'critical', label: 'Crit', bg: 'error' },
];

// A metric follows its group unless it says otherwise: healthy while the group
// is good or warning, failing when critical. Overriding this is what lets a
// warning group point at the one metric that actually went bad.
const DEFAULT_HEALTHY_AT = { good: true, warning: true, critical: false };

const healthyAt = (metric) => ({ ...DEFAULT_HEALTHY_AT, ...(metric?.healthy_at || {}) });

// Define available metric groups that can be added
const AVAILABLE_GROUPS = [
  { id: 'visual-perception', name: 'Visual Perception', level: 0 },
  { id: 'encoder', name: 'Encoder', level: 1 },
  { id: 'packager', name: 'Packager', level: 2 },
  { id: 'cdn-analytics', name: 'CDN Analytics', level: 3 },
  { id: 'origin', name: 'Origin', level: 2 },
  { id: 'transcoder', name: 'Transcoder', level: 1 },
  { id: 'drm', name: 'DRM', level: 2 },
  { id: 'manifest', name: 'Manifest', level: 2 },
];

function ScenarioBuilder({ open, template, onClose, onSave, readOnly = false,
  pathOptions = ['Channel'], pathOptionsKnown = false }) {
  // Initialize nodes and edges from template
  const initialNodes = useMemo(() => {
    if (template?.workflow_data?.nodes) {
      return template.workflow_data.nodes.map((node) => ({
        ...node,
        type: 'metricGroup',
      }));
    }
    return [];
  }, [template]);

  const initialEdges = useMemo(() => {
    if (template?.workflow_data?.edges) {
      return template.workflow_data.edges;
    }
    return [];
  }, [template]);

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [customGroupDialog, setCustomGroupDialog] = useState(false);
  const [customGroupForm, setCustomGroupForm] = useState({ name: '', level: 1 });
  // Metrics editor state
  const [metricsDialog, setMetricsDialog] = useState(false);
  const [editingMetrics, setEditingMetrics] = useState([]);

  // Global errors state - errors that apply to the whole scenario
  const initialGlobalErrors = useMemo(() => {
    const errors = template?.metric_config?.errors || {};
    return {
      abr: errors.abr || false,
      cdn: errors.cdn || false,
      origin: errors.origin || false,
      header_check: errors.header_check || false,
      black_screen: errors.black_screen || false,
      formats: errors.formats || [],
      cdns: errors.cdns || [],
    };
  }, [template]);
  const [globalErrors, setGlobalErrors] = useState(initialGlobalErrors);

  // Node types for ReactFlow
  const nodeTypes = useMemo(() => ({
    metricGroup: MetricGroupNode,
  }), []);

  // Handle node changes (drag, select, etc.)
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  // Handle edge changes
  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Handle new connections
  const onConnect = useCallback((connection) => {
    setEdges((eds) =>
      addEdge(
        {
          ...connection,
          id: `e-${connection.source}-${connection.target}`,
        },
        eds
      )
    );
  }, []);

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Update node status
  const handleStatusChange = (nodeId, newStatus) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, status: newStatus } }
          : node
      )
    );
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => ({
        ...prev,
        data: { ...prev.data, status: newStatus },
      }));
    }
  };

  // Update node level
  const handleLevelChange = (nodeId, newLevel) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, level: newLevel } }
          : node
      )
    );
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => ({
        ...prev,
        data: { ...prev.data, level: newLevel },
      }));
    }
  };

  // Names the selected node publishes to that this pipeline does not define.
  // freeSolo lets anything be typed, and an unresolvable name is dropped at push
  // time, so it has to be called out here.
  const unknownPaths = useMemo(() => {
    if (!pathOptionsKnown) return [];
    const chosen = selectedNode?.data?.paths || [];
    return chosen.filter((name) => !pathOptions.includes(name));
  }, [selectedNode, pathOptions, pathOptionsKnown]);

  // Patch arbitrary fields on a node's data, keeping the canvas and the
  // selection panel in step.
  const handleNodeDataChange = (nodeId, patch) => {
    setNodes((nds) => nds.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...patch } } : node));
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => ({ ...prev, data: { ...prev.data, ...patch } }));
    }
  };

  // Update node publish paths
  const handlePathsChange = (nodeId, newPaths) => {
    handleNodeDataChange(nodeId, { paths: newPaths });
  };

  // Open metrics editor for selected node
  const handleEditMetrics = () => {
    if (!selectedNode) return;
    const currentMetrics = selectedNode.data.metrics || [];
    setEditingMetrics([...currentMetrics]);
    setMetricsDialog(true);
  };

  // Save metrics for selected node
  const handleSaveMetrics = () => {
    if (!selectedNode) return;
    const nodeId = selectedNode.id;
    const metricsToSave = editingMetrics.length > 0 ? editingMetrics : undefined;

    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, metrics: metricsToSave } }
          : node
      )
    );
    setSelectedNode((prev) => ({
      ...prev,
      data: { ...prev.data, metrics: metricsToSave },
    }));
    setMetricsDialog(false);
    setEditingMetrics([]);
  };

  // Add a new metric to the editing list
  const handleAddMetric = () => {
    setEditingMetrics([
      ...editingMetrics,
      {
        name: '', suffix: '%', type: 'numeric',
        good: 100, warning: 50, critical: 0,
        visible: true, healthy_at: { ...DEFAULT_HEALTHY_AT },
      }
    ]);
  };

  // Update a metric in the editing list
  const handleUpdateMetric = (index, field, value) => {
    const updated = [...editingMetrics];
    updated[index] = { ...updated[index], [field]: value };
    setEditingMetrics(updated);
  };

  // Switch a metric between numeric and text, converting its band values so the
  // inputs stay usable (a number field cannot hold "Healthy").
  const handleUpdateMetricType = (index, type) => {
    const updated = [...editingMetrics];
    const metric = { ...updated[index], type };
    METRIC_BANDS.forEach(({ key }) => {
      const current = metric[key];
      if (type === 'text') {
        metric[key] = current === undefined || current === null ? '' : String(current);
      } else {
        const parsed = parseFloat(current);
        metric[key] = Number.isNaN(parsed) ? 0 : parsed;
      }
    });
    updated[index] = metric;
    setEditingMetrics(updated);
  };

  // Toggle whether a metric reads as healthy while its group sits in one band.
  const handleUpdateMetricHealthyAt = (index, band, checked) => {
    const updated = [...editingMetrics];
    updated[index] = {
      ...updated[index],
      healthy_at: { ...healthyAt(updated[index]), [band]: checked },
    };
    setEditingMetrics(updated);
  };

  // Delete a metric from the editing list
  const handleDeleteMetric = (index) => {
    setEditingMetrics(editingMetrics.filter((_, i) => i !== index));
  };

  // Handle global error toggle
  const handleGlobalErrorToggle = (errorType) => {
    setGlobalErrors((prev) => ({
      ...prev,
      [errorType]: !prev[errorType],
    }));
  };

  // Handle global format error toggle
  const handleFormatErrorToggle = (format) => {
    setGlobalErrors((prev) => {
      const formats = prev.formats || [];
      const newFormats = formats.includes(format)
        ? formats.filter((f) => f !== format)
        : [...formats, format];
      return { ...prev, formats: newFormats };
    });
  };

  // Handle global CDN-specific error change
  const handleCdnErrorsChange = (newCdns) => {
    setGlobalErrors((prev) => ({
      ...prev,
      cdns: newCdns,
    }));
  };

  // Handle per-node error toggle
  const handleNodeErrorToggle = (nodeId, errorKey) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== nodeId) return node;
        const currentErrors = node.data.errors || [];
        const newErrors = currentErrors.includes(errorKey)
          ? currentErrors.filter((e) => e !== errorKey)
          : [...currentErrors, errorKey];
        return { ...node, data: { ...node.data, errors: newErrors } };
      })
    );
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => {
        const currentErrors = prev.data.errors || [];
        const newErrors = currentErrors.includes(errorKey)
          ? currentErrors.filter((e) => e !== errorKey)
          : [...currentErrors, errorKey];
        return { ...prev, data: { ...prev.data, errors: newErrors } };
      });
    }
  };

  // Check if any errors are configured (global or per-node)
  const hasAnyErrors = useMemo(() => {
    // Check global errors
    const hasGlobalErrors =
      globalErrors.abr || globalErrors.cdn || globalErrors.origin ||
      globalErrors.header_check || globalErrors.black_screen ||
      (globalErrors.formats?.length > 0) || (globalErrors.cdns?.length > 0);

    // Check per-node errors
    const hasNodeErrors = nodes.some((node) => node.data.errors?.length > 0);

    return hasGlobalErrors || hasNodeErrors;
  }, [globalErrors, nodes]);

  // Add new node
  const handleAddNode = (groupConfig) => {
    // Check if node with this ID already exists
    if (nodes.some((n) => n.id === groupConfig.id)) {
      return;
    }

    const newNode = {
      id: groupConfig.id,
      type: 'metricGroup',
      position: {
        x: 100 + Math.random() * 400,
        y: 100 + Math.random() * 200,
      },
      data: {
        groupName: groupConfig.name,
        status: 100,
        level: groupConfig.level,
        paths: [],
        inline: true,
        update_threshold: 10,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // Create custom group
  const handleCreateCustomGroup = () => {
    if (!customGroupForm.name.trim()) return;

    const id = customGroupForm.name.toLowerCase().replace(/\s+/g, '-');

    // Check if a node with this ID or name already exists
    if (nodes.some((n) => n.id === id || n.data.groupName === customGroupForm.name)) {
      return;
    }

    handleAddNode({
      id,
      name: customGroupForm.name.trim(),
      level: customGroupForm.level,
    });

    // Reset form and close dialog
    setCustomGroupForm({ name: '', level: 1 });
    setCustomGroupDialog(false);
  };

  // Delete selected node
  const handleDeleteNode = () => {
    if (!selectedNode) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) =>
      eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id)
    );
    setSelectedNode(null);
  };

  // Save workflow
  const handleSave = () => {
    const workflowData = {
      nodes: nodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data,
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
    };

    // Include global errors in the save data
    // The parent component should merge this into metric_config.errors
    const errorsData = {
      abr: globalErrors.abr,
      cdn: globalErrors.cdn,
      origin: globalErrors.origin,
      header_check: globalErrors.header_check,
      black_screen: globalErrors.black_screen,
      formats: globalErrors.formats,
      cdns: globalErrors.cdns,
    };

    onSave(workflowData, errorsData);
  };

  // Get status label
  const getStatusLabel = (status) => {
    if (status === 100) return 'Good';
    if (status >= 50) return 'Warning';
    return 'Critical';
  };

  const getStatusColor = (status) => {
    if (status === 100) return '#4caf50';
    if (status >= 50) return '#ff9800';
    return '#f44336';
  };

  // Available groups not yet in the workflow
  const availableGroups = AVAILABLE_GROUPS.filter(
    (g) => !nodes.some((n) => n.id === g.id)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {readOnly ? 'View Workflow' : 'Edit Workflow'}: {template?.name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex' }}>
        {/* Main Canvas */}
        <Box sx={{ flexGrow: 1, height: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[20, 20]}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
          >
            <Controls />
            <Background variant="dots" gap={20} size={1} />
            {!readOnly && (
              <Panel position="top-left">
                <Paper sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Add Group:
                  </Typography>
                  {availableGroups.slice(0, 4).map((group) => (
                    <Button
                      key={group.id}
                      size="small"
                      variant="outlined"
                      onClick={() => handleAddNode(group)}
                    >
                      {group.name}
                    </Button>
                  ))}
                  {availableGroups.length > 4 && (
                    <Typography variant="body2" color="text.secondary">
                      +{availableGroups.length - 4} more
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => setCustomGroupDialog(true)}
                  >
                    Custom
                  </Button>
                </Paper>
              </Panel>
            )}
          </ReactFlow>
        </Box>

        {/* Properties Panel */}
        <Paper
          sx={{
            width: 300,
            p: 2,
            borderLeft: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Properties
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {selectedNode ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                {selectedNode.data.groupName}
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Status: {getStatusLabel(selectedNode.data.status)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Slider
                    value={selectedNode.data.status}
                    onChange={(e, value) => handleStatusChange(selectedNode.id, value)}
                    step={50}
                    marks={[
                      { value: 0, label: 'Critical' },
                      { value: 50, label: 'Warning' },
                      { value: 100, label: 'Good' },
                    ]}
                    min={0}
                    max={100}
                    disabled={readOnly}
                    sx={{
                      '& .MuiSlider-thumb': {
                        backgroundColor: getStatusColor(selectedNode.data.status),
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: getStatusColor(selectedNode.data.status),
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth size="small" disabled={readOnly}>
                  <InputLabel>Hierarchy Level</InputLabel>
                  <Select
                    value={selectedNode.data.level}
                    label="Hierarchy Level"
                    onChange={(e) => handleLevelChange(selectedNode.id, e.target.value)}
                  >
                    {[0, 1, 2, 3, 4, 5].map((level) => (
                      <MenuItem key={level} value={level}>
                        Level {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={pathOptions}
                  value={selectedNode.data.paths || []}
                  onChange={(e, val) => handlePathsChange(selectedNode.id, val)}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField {...params} label="Publish to paths"
                      placeholder={pathOptions.join(', ')}
                      error={unknownPaths.length > 0}
                      helperText={
                        unknownPaths.length > 0
                          ? `Not defined on this pipeline: ${unknownPaths.join(', ')}. Add it under E2E Integration Settings or these tiles will not be published.`
                          : pathOptionsKnown
                            ? 'Empty = default by hierarchy level'
                            : 'Empty = default by hierarchy level. Names are resolved per pipeline under E2E Integration Settings.'
                      } />
                  )}
                />
              </Box>

              {/* Tile rendering options */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedNode.data.inline !== false}
                      onChange={(e) => handleNodeDataChange(selectedNode.id, { inline: e.target.checked })}
                      disabled={readOnly}
                      size="small"
                    />
                  }
                  label="Inline tile"
                />
                <TextField
                  label="Update threshold"
                  type="number"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={selectedNode.data.update_threshold ?? 10}
                  onChange={(e) => handleNodeDataChange(selectedNode.id, {
                    update_threshold: Math.min(60, Math.max(1, parseInt(e.target.value, 10) || 10)),
                  })}
                  disabled={readOnly}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="Minutes before Touchstream marks the tile stale"
                />
              </Box>

              {/* Custom Metrics Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Custom Metrics
                </Typography>
                {selectedNode.data.metrics && selectedNode.data.metrics.length > 0 ? (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedNode.data.metrics.map(m => m.name).filter(n => n).join(', ') || 'No names defined'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Using default metrics
                  </Typography>
                )}
                {!readOnly && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<EditIcon />}
                    onClick={handleEditMetrics}
                  >
                    Edit Metrics
                  </Button>
                )}
              </Box>

              {/* Per-Node Errors Section */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ErrorIcon
                    color={selectedNode.data.errors?.length > 0 ? 'error' : 'disabled'}
                    fontSize="small"
                  />
                  <Typography variant="body2">Node Errors</Typography>
                  {selectedNode.data.errors?.length > 0 && (
                    <Chip label={selectedNode.data.errors.length} size="small" color="error" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Errors triggered when this specific node is part of the active scenario.
                </Typography>
                <FormGroup>
                  {ERROR_TYPES.slice(0, 3).map((error) => (
                    <FormControlLabel
                      key={error.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedNode.data.errors?.includes(error.id) || false}
                          onChange={() => handleNodeErrorToggle(selectedNode.id, error.id)}
                          disabled={readOnly}
                        />
                      }
                      label={<Typography variant="body2">{error.label}</Typography>}
                    />
                  ))}
                </FormGroup>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {AVAILABLE_FORMATS.map((format) => (
                    <FormControlLabel
                      key={format}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedNode.data.errors?.includes(`format:${format}`) || false}
                          onChange={() => handleNodeErrorToggle(selectedNode.id, `format:${format}`)}
                          disabled={readOnly}
                        />
                      }
                      label={<Typography variant="caption">{format.toUpperCase()}</Typography>}
                    />
                  ))}
                </Box>
              </Box>

              {!readOnly && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={handleDeleteNode}
                  >
                    Delete Node
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a node to edit its properties
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Global Errors Section */}
          <Accordion defaultExpanded={hasAnyErrors}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color={hasAnyErrors ? 'error' : 'disabled'} fontSize="small" />
                <Typography variant="subtitle2">
                  Error Simulation
                </Typography>
                {hasAnyErrors && (
                  <Chip label="Active" size="small" color="error" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Configure errors that will be triggered when this scenario is activated.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Global Errors
              </Typography>
              <FormGroup>
                {ERROR_TYPES.map((error) => (
                  <FormControlLabel
                    key={error.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={globalErrors[error.id] || false}
                        onChange={() => handleGlobalErrorToggle(error.id)}
                        disabled={readOnly}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{error.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {error.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Format-Specific Errors
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {AVAILABLE_FORMATS.map((format) => (
                    <FormControlLabel
                      key={format}
                      control={
                        <Checkbox
                          size="small"
                          checked={globalErrors.formats?.includes(format) || false}
                          onChange={() => handleFormatErrorToggle(format)}
                          disabled={readOnly}
                        />
                      }
                      label={format.toUpperCase()}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  CDN-Specific Errors
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={AVAILABLE_CDNS}
                  value={globalErrors.cdns || []}
                  onChange={(e, newValue) => handleCdnErrorsChange(newValue)}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select CDNs..."
                      variant="outlined"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            {readOnly ? 'Viewing Mode' : 'Instructions'}
          </Typography>
          {readOnly ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - You are viewing this workflow in read-only mode
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - Click on nodes to view their properties
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - Contact a Super Admin to make changes
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - Click and drag nodes to reposition
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - Drag from node handle to create connections
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - Select a node to edit status (0=Critical, 50=Warning, 100=Good)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - Use the buttons above to add new metric groups
              </Typography>
            </>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Workflow
          </Button>
        )}
      </DialogActions>

      {/* Custom Group Creation Dialog */}
      <Dialog
        open={customGroupDialog}
        onClose={() => setCustomGroupDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Custom Group</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              label="Group Name"
              fullWidth
              value={customGroupForm.name}
              onChange={(e) => setCustomGroupForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Ad Server, Transcoder, Analytics"
              helperText="Enter any custom name for your metric group"
            />
            <FormControl fullWidth>
              <InputLabel>Hierarchy Level</InputLabel>
              <Select
                value={customGroupForm.level}
                label="Hierarchy Level"
                onChange={(e) => setCustomGroupForm((prev) => ({ ...prev, level: e.target.value }))}
              >
                {[0, 1, 2, 3, 4, 5].map((level) => (
                  <MenuItem key={level} value={level}>
                    Level {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomGroupDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomGroup}
            disabled={!customGroupForm.name.trim()}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metrics Editor Dialog */}
      <Dialog
        open={metricsDialog}
        onClose={() => setMetricsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Metrics: {selectedNode?.data?.groupName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define custom metrics for this node. These will override pipeline defaults when this scenario is active.
              Leave empty to use default metrics.
            </Typography>

            {editingMetrics.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No custom metrics defined. Default metrics will be used.
              </Typography>
            ) : (
              editingMetrics.map((metric, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      label="Metric Name"
                      value={metric.name}
                      onChange={(e) => handleUpdateMetric(index, 'name', e.target.value)}
                      size="small"
                      sx={{ flex: '1 1 150px' }}
                      placeholder="e.g., Bitrate"
                    />
                    <FormControl size="small" sx={{ width: 110 }}>
                      <InputLabel id={`metric-type-label-${index}`}>Type</InputLabel>
                      <Select
                        labelId={`metric-type-label-${index}`}
                        id={`metric-type-${index}`}
                        label="Type"
                        value={metric.type || 'numeric'}
                        onChange={(e) => handleUpdateMetricType(index, e.target.value)}
                      >
                        <MenuItem value="numeric">Numeric</MenuItem>
                        <MenuItem value="text">Text</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Suffix"
                      value={metric.suffix}
                      onChange={(e) => handleUpdateMetric(index, 'suffix', e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      placeholder="%"
                    />
                    {METRIC_BANDS.map(({ key, label, bg }) => (
                      <TextField
                        key={key}
                        label={label}
                        type={metric.type === 'text' ? 'text' : 'number'}
                        value={metric[key] ?? ''}
                        onChange={(e) => handleUpdateMetric(
                          index,
                          key,
                          metric.type === 'text' ? e.target.value : (parseFloat(e.target.value) || 0),
                        )}
                        size="small"
                        sx={{ width: metric.type === 'text' ? 120 : 80 }}
                        InputProps={{ sx: { bgcolor: `${bg}.light`, color: `${bg}.contrastText` } }}
                      />
                    ))}
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteMetric(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      Shows as healthy when the group is:
                    </Typography>
                    <FormGroup row>
                      {METRIC_BANDS.map(({ key, label }) => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              size="small"
                              checked={healthyAt(metric)[key]}
                              onChange={(e) => handleUpdateMetricHealthyAt(index, key, e.target.checked)}
                              inputProps={{ 'aria-label': `Healthy when ${label}` }}
                            />
                          }
                          label={<Typography variant="caption">{label}</Typography>}
                        />
                      ))}
                    </FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={metric.visible !== false}
                          onChange={(e) => handleUpdateMetric(index, 'visible', e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Visible on tile</Typography>}
                    />
                  </Box>
                </Box>
              ))
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddMetric}
              sx={{ mt: 1 }}
            >
              Add Metric
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetricsDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMetrics}
          >
            Save Metrics
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default ScenarioBuilder;
